import { useEffect, useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Checkbox, Creatable, Select } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { JsonInput } from '@/common/components/JsonInput'
import { useAllApplications } from '../../../../../common/hooks/useAllApplications'
import { useManageApplicationsAccount } from '../hooks/useManageApplicationsAccount'
import { useNewApplicationAccount } from '../hooks/useNewApplicationAccount'
import {
  ApplicationAccountForm,
  ApplicationAccountSchema,
} from '../schemas/ApplicationAccountSchema'

export function FormLinkApplicationAccount() {
  const { applications, isLoadingApplications } = useAllApplications()
  const { t } = useTranslation('sso')
  const { ACCOUNT_SELECTED } = useManageApplicationsAccount()
  const { handleCreateApplicationAccount, isCreatingApplicationAccount } =
    useNewApplicationAccount()
  const hookForm = useForm<ApplicationAccountForm>({
    resolver: zodResolver(ApplicationAccountSchema()),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      applicationId: '',
      accountFields: undefined,
      tag: [],
      isMainAccount: false,
    },
  })

  const handleSubmit = async (data: ApplicationAccountForm) => {
    if (!ACCOUNT_SELECTED) return

    await handleCreateApplicationAccount(ACCOUNT_SELECTED.id, data).then(() => {
      hookForm.reset()
    })
  }

  const handleCancel = () => {
    hookForm.reset()
  }

  const APPLICATION_OPTIONS = useMemo(
    () =>
      applications
        .map((application) => ({
          value: application.id,
          label: application.title,
        }))
        .filter((application) => {
          const isAlreadyLinkedToAccount =
            ACCOUNT_SELECTED?.ApplicationAccount?.some(
              (accountApp) => accountApp.applicationId === application.value,
            )

          return !isAlreadyLinkedToAccount
        }),
    [applications, ACCOUNT_SELECTED],
  )

  const APPLICATION_ACCOUNT_SELECTED = hookForm.watch('applicationId')

  useEffect(() => {
    if (!ACCOUNT_SELECTED) hookForm.reset()
  }, [ACCOUNT_SELECTED, hookForm])

  return (
    <FormProvider {...hookForm}>
      <form
        className="space-y-6"
        onSubmit={hookForm.handleSubmit(handleSubmit)}
      >
        <Select.Single
          name="applicationId"
          label={t(
            'accounts.drawer_manage_applications.fields.applicationId.label',
          )}
          placeholder={t(
            'accounts.drawer_manage_applications.fields.applicationId.placeholder',
          )}
          disabled={isLoadingApplications}
          options={APPLICATION_OPTIONS}
          noOptionsText={t(
            'accounts.drawer_manage_applications.fields.applicationId.noOptionsText',
          )}
        />

        {APPLICATION_ACCOUNT_SELECTED && (
          <>
            <Creatable.Sync
              isMulti
              icon={null}
              name="tag"
              label={t('accounts.drawer_manage_applications.fields.tag.label')}
              placeholder={t(
                'accounts.drawer_manage_applications.fields.tag.placeholder',
              )}
              formatCreateLabel={(value) =>
                t(
                  'accounts.drawer_manage_applications.fields.tag.formatCreateLabel',
                  { value },
                )
              }
            />

            <JsonInput
              name="accountFields"
              label={t(
                'accounts.drawer_manage_applications.fields.accountFields.label',
              )}
              placeholder={t(
                'accounts.drawer_manage_applications.fields.accountFields.placeholder',
              )}
            />

            <Checkbox
              name="isMainAccount"
              text={t(
                'accounts.drawer_manage_applications.fields.isMainAccount.label',
              )}
            />

            <div className="flex w-full gap-2 items-center">
              <Button.Root
                variant="outline"
                shape="pill"
                onClick={handleCancel}
              >
                <Button.Icon icon={X} />
                {t(
                  'accounts.drawer_manage_applications.fields.button_cancel.label',
                )}
              </Button.Root>

              <Button.Root
                variant="outline"
                shape="pill"
                type="submit"
                isLoading={isCreatingApplicationAccount}
                disabled={isCreatingApplicationAccount}
              >
                <Button.Icon icon={Plus} />
                {t(
                  'accounts.drawer_manage_applications.fields.button_add.label',
                )}
              </Button.Root>
            </div>
          </>
        )}
      </form>
    </FormProvider>
  )
}
